// Dates, deadlines and progress of the team board. Due dates are calendar days ('YYYY-MM-DD')
// without a time, so everything here counts whole local days.

import { EVERYONE } from '../../constants/team';

export const STATUSES = [
  { id: 'OPEN', label: 'Offen', accent: '#9ca3af' },
  { id: 'IN_PROGRESS', label: 'In Arbeit', accent: '#006cfd' },
  { id: 'DONE', label: 'Erledigt', accent: '#16a34a' },
];

const DAY_MS = 86400000;
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const pad = (n) => String(n).padStart(2, '0');

/** Today (or any date) as 'YYYY-MM-DD' in local time. */
export const localIso = (date = new Date()) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const parts = (iso) => iso.split('-').map(Number);

/** Whole days from today to the date: 0 today, 1 tomorrow, -2 the day before yesterday. */
export const daysUntil = (iso, today = new Date()) => {
  if (!iso) return null;
  const [y, m, d] = parts(iso);
  return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())) / DAY_MS);
};

/** "Fr 24.10.", with the year when it is not this one. */
export const formatDay = (iso, today = new Date()) => {
  if (!iso) return '';
  const [y, m, d] = parts(iso);
  const weekday = WEEKDAYS[new Date(y, m - 1, d).getDay()];
  return `${weekday} ${pad(d)}.${pad(m)}.${y === today.getFullYear() ? '' : y}`;
};

/**
 * How much time is left until a date, in words, and how pressing it is:
 * overdue, today, soon (within three days) or later.
 */
export const timeLeft = (iso, today = new Date()) => {
  const days = daysUntil(iso, today);
  if (days == null) return null;
  if (days < -1) return { days, tone: 'overdue', text: `seit ${-days} Tagen überfällig` };
  if (days === -1) return { days, tone: 'overdue', text: 'seit gestern überfällig' };
  if (days === 0) return { days, tone: 'today', text: 'heute fällig' };
  if (days === 1) return { days, tone: 'soon', text: 'morgen fällig' };
  return { days, tone: days <= 3 ? 'soon' : 'later', text: `noch ${days} Tage` };
};

export const isOpen = (task) => task.status !== 'DONE';

const byDue = (a, b) => (a.dueDate || '9999').localeCompare(b.dueDate || '9999') || a.id - b.id;

/** Open tasks that have a date, the most pressing first. */
export const upcoming = (tasks) => tasks.filter((t) => isOpen(t) && t.dueDate).sort(byDue);

/** Open tasks that are overdue or due within the next `days` days. */
export const dueWithin = (tasks, days = 7, today = new Date()) =>
  upcoming(tasks).filter((t) => daysUntil(t.dueDate, today) <= days);

export const overdue = (tasks, today = new Date()) =>
  upcoming(tasks).filter((t) => daysUntil(t.dueDate, today) < 0);

/** A column's cards: open ones by due date (undated last), done ones the latest first. */
export const columnTasks = (tasks, status) => {
  const own = tasks.filter((t) => t.status === status);
  if (status !== 'DONE') return own.sort(byDue);
  return own.sort((a, b) => (b.doneAt || '').localeCompare(a.doneAt || '') || b.id - a.id);
};

/** A person's view includes what the whole team shares. */
export const belongsTo = (task, person) => !person || task.assignee === person || task.assignee === EVERYONE;

export const progress = (milestoneId, tasks) => {
  const own = tasks.filter((t) => t.milestoneId === milestoneId);
  const done = own.filter((t) => t.status === 'DONE').length;
  return { done, total: own.length, pct: own.length ? Math.round((done / own.length) * 100) : 0 };
};

/** Reached once it has tasks and all of them are done. */
export const isReached = (milestone, tasks) => {
  const { done, total } = progress(milestone.id, tasks);
  return total > 0 && done === total;
};

const byDeadline = (a, b) => a.dueDate.localeCompare(b.dueDate) || a.id - b.id;

/** The milestone to work towards: the earliest one not reached yet. */
export const nextMilestone = (milestones, tasks) =>
  milestones.filter((m) => !isReached(m, tasks)).sort(byDeadline)[0] || null;

/** Milestones still ahead and those reached, each by deadline. */
export const splitMilestones = (milestones, tasks) => ({
  active: milestones.filter((m) => !isReached(m, tasks)).sort(byDeadline),
  reached: milestones.filter((m) => isReached(m, tasks)).sort(byDeadline),
});
